/**
 * In-world indicators (08 §2.7, §4), drawn as SVG and DOM over the game image: detection arcs
 * around the ranger, his noise ring, and sound pips at the screen edge. This file is the view;
 * the decisions are in detection.ts, noise.ts and pips.ts, the ground -> pixels map in
 * projection.ts. Nothing here takes pointer input.
 */

import './layer.css';
import { closing } from '../../../audio/dogVoice';
import type { Biome, SimEvent, World } from '../../../sim/types';
import { el, show, Shown } from '../dom';
import { type RingMode, type StepRingMemory, stepRingShows } from '../hudAids';
import { type ArcSpec, arcShape, detectionArcs, MAX_ARCS } from './detection';
import {
  eventNoise, footstepRadius, loudest, type NoiseSource, type OwnOrdnance, ownOrdnance, ringStrength, STEP_RING_MS,
} from './noise';
import {
  addPip, hearingRange, loudness, MAX_PIPS, type Pip, PIP_LABEL, PIP_MS, type PipKind, pipKey, pipSource, placePip,
  PLAIN_TO_SEE,
} from './pips';
import {
  type GroundBasis, groundBasis, type HudFrame, type Point, type Projector, ringRadii, toHud,
} from './projection';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Pips keep this far inside the image, so their labels stay on it. */
const PIP_INSET = 20;
/** The noise figure sits this far under the ranger's feet, pixels. */
const NOISE_LABEL_DROP = 16;
/** Events held for the next `update`. */
const QUEUE_MAX = 256;

/** Already resolved by `Hud.setOptions`: the player's settings crossed with the difficulty (hudAids.ts). */
export interface WorldLayerOptions {
  arcs: boolean;
  ring: RingMode;
  soundPips: boolean;
}

function svg(tag: string, className: string): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  node.setAttribute('class', className);
  node.style.display = 'none';
  return node;
}

/** Whole-pixel placement; the stylesheet centres the element on the point. */
function moveTo(node: HTMLElement, at: Point): void {
  node.style.left = `${Math.round(at.x)}px`;
  node.style.top = `${Math.round(at.y)}px`;
}

interface ArcView {
  track: SVGElement;
  lit: SVGElement;
  glyph: HTMLElement;
}

interface PipView {
  box: HTMLElement;
  chevron: HTMLElement;
  label: HTMLElement;
}

export class WorldLayer {
  readonly el: HTMLElement;
  private readonly arcs: ArcView[] = [];
  private readonly ring: SVGElement;
  private readonly ringLabel: HTMLElement;
  private readonly pipViews: PipView[] = [];
  private readonly shown = new Shown();

  private options: WorldLayerOptions = { arcs: true, ring: 'flash', soundPips: true };
  private projector: Projector | null = null;
  private frame: HudFrame = { left: 0, top: 0, scale: 1 };
  private width = 640;
  private height = 360;

  private readonly queue: SimEvent[] = [];
  private readonly noises: NoiseSource[] = [];
  private pips: Pip[] = [];
  private ordnance: OwnOrdnance[] = [];
  private lastFootstepTicks = -1;
  /** The last footstep ring raised, so a `flash` ring can tell a new lesson from the same one repeating. */
  private lastStepRing: StepRingMemory = { radius: 0, at: Number.NEGATIVE_INFINITY };

  /** R5: the noise ring is in metres a soldier can hear over, and snow swallows a third of them. */
  constructor(private readonly biome: Biome = 'temperate') {
    this.el = el('div', 'hud-world');
    const canvas = document.createElementNS(SVG_NS, 'svg');
    canvas.setAttribute('class', 'hud-world-svg');
    this.ring = svg('ellipse', 'hud-noise-ring');
    canvas.append(this.ring);
    this.el.append(canvas);
    for (let i = 0; i < MAX_ARCS; i++) {
      const view: ArcView = {
        track: svg('path', 'hud-arc-track'),
        lit: svg('path', 'hud-arc-lit'),
        glyph: el('span', 'hud-arc-glyph hud-hidden'),
      };
      canvas.append(view.track, view.lit);
      this.el.append(view.glyph);
      this.arcs.push(view);
    }
    this.ringLabel = el('span', 'hud-noise-label hud-hidden');
    this.el.append(this.ringLabel);
    for (let i = 0; i < MAX_PIPS; i++) {
      const view: PipView = {
        box: el('div', 'hud-snd hud-hidden'),
        chevron: el('span', 'hud-snd-chevron'),
        label: el('span', 'hud-snd-label'),
      };
      view.box.append(view.chevron, view.label);
      this.el.append(view.box);
      this.pipViews.push(view);
    }
  }

  setProjector(projector: Projector | null): void {
    this.projector = projector;
  }

  setOptions(options: Partial<WorldLayerOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /** Where the HUD sits in the viewport (from `Hud.layout`), and its logical size. */
  setFrame(frame: HudFrame, width: number, height: number): void {
    this.frame = frame;
    this.width = width;
    this.height = height;
  }

  /** Events are kept until `update` brings the world they happened in. */
  handle(events: readonly SimEvent[]): void {
    for (const ev of events) this.queue.push(ev);
    // Nobody is calling `update`: old news is no use, and the queue must not grow without end.
    if (this.queue.length > QUEUE_MAX) this.queue.splice(0, this.queue.length - QUEUE_MAX);
  }

  /** A new world (restart): nothing from the old one may linger. */
  clear(): void {
    this.queue.length = 0;
    this.noises.length = 0;
    this.pips = [];
    this.ordnance = [];
    this.lastFootstepTicks = -1;
    this.lastStepRing = { radius: 0, at: Number.NEGATIVE_INFINITY };
  }

  update(world: World, now: number): void {
    const ranger = world.ranger;
    const live = ranger.status === 'active' || ranger.status === 'downed';
    const basis = live && this.projector !== null
      ? groundBasis(this.projector, ranger.x, ranger.z, this.frame)
      : null;

    this.listen(world, basis, now);
    this.ordnance = ownOrdnance(world);
    this.pips = this.pips.filter((p) => now < p.until);

    const options = this.options;
    const arcs = basis !== null && options.arcs
      ? detectionArcs(world.enemies, ranger.x, ranger.z, basis)
      : [];
    this.drawArcs(arcs, basis);
    // `loudest` also prunes, so it runs whether or not the ring is wanted.
    const noise = loudest(this.noises, now);
    this.drawRing(options.ring === 'off' ? null : noise, basis, now);
    this.drawPips(basis !== null && options.soundPips ? this.pips : []);
  }

  /** Turn this frame's events, his footsteps and the Osprey into noise sources and pips. */
  private listen(world: World, basis: GroundBasis | null, now: number): void {
    const ranger = world.ranger;
    for (const ev of this.queue) {
      const noise = eventNoise(ev, { ranger, ordnance: this.ordnance, now, biome: this.biome });
      if (noise !== null) this.noises.push(noise);
      const sound = pipSource(ev);
      if (sound !== null) this.hear(sound.kind, sound.x, sound.z, world, basis, now);
    }
    this.queue.length = 0;
    // ROUND8: a dog closing on his trail growls - a sound in state, not an event (audio/dogVoice.ts): heard here.
    for (const e of world.enemies) if (closing(e, ranger.x, ranger.z)) this.hear('dog', e.x, e.z, world, basis, now);

    // `footstepTicks` wraps to zero on the tick the sim makes a footstep noise.
    const stepped = this.lastFootstepTicks >= 0 && ranger.footstepTicks < this.lastFootstepTicks;
    this.lastFootstepTicks = ranger.footstepTicks;
    const radius = stepped ? footstepRadius(ranger, world.mods, this.biome) : 0;
    // Above Milk Run a footstep only rings when it says something new: started moving, changed gait.
    if (stepRingShows(this.options.ring, radius, this.lastStepRing, now)) {
      this.lastStepRing = { radius, at: now };
      this.noises.push({ x: ranger.x, z: ranger.z, follow: true, radius, at: now, until: now + STEP_RING_MS });
    }

    const phase = world.osprey.phase;
    if (phase === 'inbound' || phase === 'hovering') {
      this.hear('osprey', world.objective.pickupX, world.objective.pickupZ, world, basis, now);
    }
  }

  private hear(kind: PipKind, x: number, z: number, world: World, basis: GroundBasis | null, now: number): void {
    if (basis === null) return;
    const ranger = world.ranger;
    const loud = loudness(Math.hypot(x - ranger.x, z - ranger.z), hearingRange(kind, world.mods));
    if (loud <= 0) return;
    const place = placePip(basis.origin, toHud(basis, x, z), this.width, this.height, PIP_INSET);
    // On-screen gunfire, blasts and the hovering Osprey are plain to see; a voice and a dog's bark are not.
    if (place.onScreen && PLAIN_TO_SEE[kind]) return;
    addPip(this.pips, { key: pipKey(kind, place.angle), kind, place, loud, until: now + PIP_MS });
  }

  private drawArcs(specs: readonly ArcSpec[], basis: GroundBasis | null): void {
    const radii = basis !== null ? ringRadii(basis, 1) : { rx: 1, ry: 1 };
    const squash = radii.rx > 0 ? radii.ry / radii.rx : 1;
    this.arcs.forEach((view, i) => {
      const spec = basis !== null ? specs[i] : undefined;
      if (this.shown.changed(`arcOn${i}`, spec !== undefined)) {
        view.track.style.display = spec !== undefined ? '' : 'none';
        view.lit.style.display = spec !== undefined ? '' : 'none';
        show(view.glyph, spec !== undefined);
      }
      if (spec === undefined || basis === null) return;
      const shape = arcShape(spec, basis.origin, squash);
      if (this.shown.changed(`arcTrack${i}`, shape.track)) view.track.setAttribute('d', shape.track);
      if (this.shown.changed(`arcLit${i}`, shape.lit)) view.lit.setAttribute('d', shape.lit);
      if (this.shown.changed(`arcAlert${i}`, spec.alert)) {
        view.lit.setAttribute('class', spec.alert ? 'hud-arc-lit hud-arc-alert' : 'hud-arc-lit');
        view.glyph.classList.toggle('hud-arc-alert', spec.alert);
        view.glyph.textContent = spec.glyph;
      }
      if (this.shown.changed(`arcGlyph${i}`, `${shape.glyph.x},${shape.glyph.y}`)) moveTo(view.glyph, shape.glyph);
    });
  }

  private drawRing(source: NoiseSource | null, basis: GroundBasis | null, now: number): void {
    const on = source !== null && basis !== null;
    if (this.shown.changed('ringOn', on)) {
      this.ring.style.display = on ? '' : 'none';
      show(this.ringLabel, on);
    }
    if (source === null || basis === null) return;
    const centre = source.follow ? basis.origin : toHud(basis, source.x, source.z);
    const { rx, ry } = ringRadii(basis, source.radius);
    const key = `${Math.round(centre.x)},${Math.round(centre.y)},${Math.round(rx)},${Math.round(ry)}`;
    if (this.shown.changed('ringShape', key)) {
      this.ring.setAttribute('cx', String(Math.round(centre.x)));
      this.ring.setAttribute('cy', String(Math.round(centre.y)));
      this.ring.setAttribute('rx', String(Math.round(rx)));
      this.ring.setAttribute('ry', String(Math.round(ry)));
    }
    const fade = Math.round(ringStrength(source, now) * 5) / 5;
    if (this.shown.changed('ringFade', fade)) this.ring.style.opacity = String(0.25 + 0.6 * fade);
    // Most of a gunshot's ring is off the screen: the figure says what the line cannot.
    const metres = Math.round(source.radius);
    if (this.shown.changed('ringMetres', metres)) this.ringLabel.textContent = `NOISE ${metres}M`;
    const under = { x: basis.origin.x, y: basis.origin.y + NOISE_LABEL_DROP };
    const labelAt = `${Math.round(under.x)},${Math.round(under.y)}`;
    if (this.shown.changed('ringLabelAt', labelAt)) moveTo(this.ringLabel, under);
  }

  private drawPips(pips: readonly Pip[]): void {
    this.pipViews.forEach((view, i) => {
      const pip = pips[i];
      if (this.shown.changed(`pipOn${i}`, pip !== undefined)) show(view.box, pip !== undefined);
      if (pip === undefined) return;
      if (this.shown.changed(`pipKind${i}`, pip.kind)) {
        view.box.dataset['kind'] = pip.kind;
        view.label.textContent = PIP_LABEL[pip.kind];
      }
      const level = pip.loud > 0.66 ? 3 : pip.loud > 0.33 ? 2 : 1;
      if (this.shown.changed(`pipLevel${i}`, level)) view.box.dataset['level'] = String(level);
      const at = `${Math.round(pip.place.x)},${Math.round(pip.place.y)}`;
      if (this.shown.changed(`pipAt${i}`, at)) {
        moveTo(view.box, pip.place);
        // The label goes on the side with room: above a pip in the lower half, below one in the upper.
        view.box.classList.toggle('hud-snd-low', pip.place.y > this.height / 2);
      }
      const turn = Math.round((pip.place.angle * 180) / Math.PI / 22.5) * 22.5;
      if (this.shown.changed(`pipTurn${i}`, turn)) view.chevron.style.transform = `rotate(${turn}deg)`;
      if (this.shown.changed(`pipHere${i}`, pip.place.onScreen)) show(view.chevron, !pip.place.onScreen);
    });
  }
}
